import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
// Story 0-6: Use Supabase-based auth hook
import { useEmailAuth } from '@/features/auth/hooks/useEmailAuth';
import { toast } from 'react-hot-toast';

const MerchantUsernameLogin: React.FC = () => {
	const { t } = useTranslation();
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});
	const navigate = useNavigate();

	// Story 0-6: Use Supabase-based email/password auth
	const { signIn, isLoading } = useEmailAuth();

	const handleSubmit = async () => {
		setFieldErrors({});

		// Basic validation
		if (!username.trim()) {
			setFieldErrors({ username: t('merchant.login.validation.usernameRequired') });
			return;
		}

		if (!password) {
			setFieldErrors({ password: t('merchant.login.validation.passwordRequired') });
			return;
		}

		// Story 0-6: Use Supabase signInWithPassword
		// Note: username is treated as email for Supabase auth
		const result = await signIn(username.trim(), password);

		if (result.success) {
			toast.success(t('merchant.login.success'));
			void navigate('/merchant/dashboard');
		} else {
			// Handle errors
			const errorMessage = result.error || '';
			toast.error(errorMessage || t('merchant.login.error'));

			if (errorMessage.includes('email')) {
				setFieldErrors({ username: errorMessage, password: '' });
			} else if (errorMessage.includes('password')) {
				setFieldErrors({ username: '', password: errorMessage });
			} else {
				setFieldErrors({ username: '', password: errorMessage });
			}
		}
	};

	const validateUsername = (value: string): string | null => {
		if (!value.trim()) return t('merchant.login.validation.usernameRequired');
		if (value.length < 3) return t('merchant.login.validation.usernameMinLength');
		// Email addresses can be up to 254 characters (RFC 5321)
		if (value.length > 254) return t('merchant.login.validation.usernameMaxLength');
		// Allow email format: alphanumeric, dots, underscores, hyphens, plus, and @ symbol
		if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
			return t('merchant.login.validation.emailInvalid');
		}
		return null;
	};

	const validatePassword = (value: string): string | null => {
		if (!value) return t('merchant.login.validation.passwordRequired');

		return null;
	};

	const usernameError = username ? validateUsername(username) : null;
	const passwordError = password ? validatePassword(password) : null;
	const isFormValid =
		usernameError === null &&
		passwordError === null &&
		username.trim().length > 0 &&
		password.length > 0;

	const onFormSubmit: React.FormEventHandler<HTMLFormElement> = e => {
		e.preventDefault();
		void handleSubmit();
	};

	return (
		<div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
			{/* Language Switcher - positioned at top right */}
			<div className='absolute top-4 right-4'>
				<LanguageSwitcher />
			</div>

			<div className='max-w-md w-full space-y-8'>
				<div>
					<div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100'>
						<svg className='h-6 w-6 text-blue-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-2 4h2M7 7h2v2H7V7zm0 4h2v2H7v-2z'
							/>
						</svg>
					</div>
					<h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
						{t('merchant.login.title')}
					</h2>
					<p className='mt-2 text-center text-sm text-gray-600'>{t('merchant.login.subtitle')}</p>
				</div>

				<form className='mt-8 space-y-6' onSubmit={onFormSubmit}>
					<div className='space-y-4'>
						<div>
							<label htmlFor='username' className='block text-sm font-medium text-gray-700'>
								{t('merchant.login.usernameLabel')}
							</label>
							<input
								id='username'
								name='username'
								type='text'
								autoComplete='username'
								required
								value={username}
								onChange={e => setUsername(e.target.value)}
								className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
									usernameError ? 'border-red-300' : 'border-gray-300'
								} placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
								placeholder={t('merchant.login.usernamePlaceholder')}
								disabled={isLoading}
							/>
							{(usernameError || fieldErrors.username) && (
								<p className='mt-1 text-sm text-red-600'>{fieldErrors.username || usernameError}</p>
							)}
						</div>

						<div>
							<label htmlFor='password' className='block text-sm font-medium text-gray-700'>
								{t('merchant.login.passwordLabel')}
							</label>
							<div className='mt-1 relative'>
								<input
									id='password'
									name='password'
									type={showPassword ? 'text' : 'password'}
									autoComplete='current-password'
									required
									value={password}
									onChange={e => setPassword(e.target.value)}
									className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
										passwordError ? 'border-red-300' : 'border-gray-300'
									} placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
									placeholder={t('merchant.login.passwordPlaceholder')}
									disabled={isLoading}
								/>
								<button
									type='button'
									className='absolute inset-y-0 right-0 pr-3 flex items-center'
									onClick={() => setShowPassword(!showPassword)}
									disabled={isLoading}
								>
									{showPassword ? (
										<svg
											className='h-5 w-5 text-gray-400'
											fill='none'
											viewBox='0 0 24 24'
											stroke='currentColor'
										>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
											/>
										</svg>
									) : (
										<svg
											className='h-5 w-5 text-gray-400'
											fill='none'
											viewBox='0 0 24 24'
											stroke='currentColor'
										>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
											/>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
											/>
										</svg>
									)}
								</button>
							</div>
							{(passwordError || fieldErrors.password) && (
								<p className='mt-1 text-sm text-red-600'>{fieldErrors.password || passwordError}</p>
							)}
						</div>
					</div>

					<div>
						<button
							type='submit'
							disabled={!isFormValid || isLoading}
							className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
								!isFormValid || isLoading
									? 'bg-gray-400 cursor-not-allowed'
									: 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
							}`}
						>
							{isLoading ? (
								<>
									<svg
										className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
										xmlns='http://www.w3.org/2000/svg'
										fill='none'
										viewBox='0 0 24 24'
									>
										<circle
											className='opacity-25'
											cx='12'
											cy='12'
											r='10'
											stroke='currentColor'
											strokeWidth='4'
										></circle>
										<path
											className='opacity-75'
											fill='currentColor'
											d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
										></path>
									</svg>
									{t('merchant.login.signingIn')}
								</>
							) : (
								t('merchant.login.signInButton')
							)}
						</button>
					</div>

					<div className='text-center'>
						<p className='text-sm text-gray-600'>
							{t('merchant.login.needHelp')}{' '}
							<span className='font-medium text-blue-600'>{t('merchant.login.contactAdmin')}</span>
						</p>
					</div>
				</form>
			</div>
		</div>
	);
};

export default MerchantUsernameLogin;
